/**
 * Simulated GET /api/qb-time — Company → Labor, payroll half.
 *
 * Shape is `TimeStatus` / `TimeSnapshotRow` / `TimePayload` from
 * src/lib/api/qbFinancials.ts, one snapshot per period key the tab reads
 * (thisWeek, lastWeek, thisMonth, lastMonth, ytd).
 *
 * Derived from the snapshot
 *   • The roster is real: every employee that appears in time_clock_snapshots
 *     (with their real qbtime_user_id) plus every active profile.
 *   • Whether someone is a shop or a field body follows their real clock rows —
 *     `is_shop`, or a jobcode with no job number, which in QuickBooks Time is
 *     the shop jobcode id 3668547.
 *   • Division revenue on the direct-labor table is real job_invoices revenue.
 *
 * Generated
 *   • Hours. One deterministic day value per (employee, date) seeded from their
 *     name, so the same person's week is identical on every reload and the
 *     period cards agree with each other. Over 40 h in a week becomes overtime;
 *     an unpaid 30-minute lunch is deducted on the WEEKLY views only (that is
 *     how the payroll report is read) and left in on month/YTD.
 *   • Pay rates, and therefore estimated cost (hours × rate, OT at 1.5×).
 */
import { loadTable } from '~/demo/data';
import { once, seededFloat } from './prng';
import { nyDate, revenueModel, round2, str } from './finance';

/** QuickBooks Time jobcode id for shop (non-job) time — owner's spec constant. */
export const SHOP_JOBCODE_ID = '3668547';
const OT_THRESHOLD_HOURS = 40;
const UNPAID_LUNCH_HOURS = 0.5;
const OT_MULTIPLIER = 1.5;

export type PeriodKey = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'ytd';
const PERIODS: PeriodKey[] = ['thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'ytd'];

interface Employee {
  user_id: string;
  name: string;
  qbtime_user_id: string;
  shop: boolean;
  pay_rate: number;
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = out.getDay(); // 0 Sun … 6 Sat
  out.setDate(out.getDate() + (dow === 0 ? -6 : 1 - dow));
  return out;
}

function periodBounds(key: PeriodKey, today: Date): { start: Date; end: Date } {
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const thisMonday = startOfWeek(midnight);
  switch (key) {
    case 'thisWeek': return { start: thisMonday, end: midnight };
    case 'lastWeek': {
      const s = new Date(thisMonday); s.setDate(s.getDate() - 7);
      const e = new Date(thisMonday); e.setDate(e.getDate() - 1);
      return { start: s, end: e };
    }
    case 'thisMonth':
      return { start: new Date(midnight.getFullYear(), midnight.getMonth(), 1), end: midnight };
    case 'lastMonth':
      return {
        start: new Date(midnight.getFullYear(), midnight.getMonth() - 1, 1),
        end: new Date(midnight.getFullYear(), midnight.getMonth(), 0),
      };
    case 'ytd':
      return { start: new Date(midnight.getFullYear(), 0, 1), end: midnight };
  }
}

/** Weekday dates in [start, end], inclusive. */
function weekdays(start: Date, end: Date): Date[] {
  const out: Date[] = [];
  const d = new Date(start);
  while (d <= end && out.length < 400) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/** Hours one person worked one day — the single source of every hours figure. */
function dayHours(emp: Employee, date: string): { worked: number; pto: number } {
  const roll = seededFloat(`${emp.name}|${date}|roll`, 0, 1);
  if (roll < 0.03) return { worked: 0, pto: 8 };          // PTO day
  if (roll < 0.06) return { worked: 0, pto: 0 };           // unscheduled
  const base = emp.shop ? seededFloat(`${emp.name}|${date}|h`, 7.4, 9.1)
    : seededFloat(`${emp.name}|${date}|h`, 7.6, 10.4);
  return { worked: round2(base), pto: 0 };
}

const roster = once(async (): Promise<Employee[]> => {
  const [snaps, profiles] = await Promise.all([loadTable('time_clock_snapshots'), loadTable('profiles')]);

  const byName = new Map<string, Employee>();
  const shopHint = new Map<string, boolean>();
  const qbIds = new Map<string, string>();
  for (const r of snaps) {
    const name = str(r.employee_name).replace(/\s*\(\d+\)\s*$/, '').trim();
    if (!name) continue;
    // Shop time in QuickBooks Time is the jobcode with no job number attached
    // (jobcode id 3668547); `is_shop` is the poller's own flag for the same idea.
    const isShop = r.is_shop === true || !str(r.job_number);
    shopHint.set(name, (shopHint.get(name) ?? false) || isShop);
    if (str(r.qbtime_user_id)) qbIds.set(name, str(r.qbtime_user_id));
  }

  for (const p of profiles) {
    if (p.is_active === false) continue;
    const name = str(p.display_name) || str(p.email);
    if (!name) continue;
    const shop = shopHint.get(name) ?? seededFloat(`${name}|shop`, 0, 1) < 0.3;
    byName.set(name, {
      user_id: str(p.user_id),
      name,
      qbtime_user_id: qbIds.get(name) ?? String(3_000_000 + Math.floor(seededFloat(`${name}|qbid`, 0, 999_999))),
      shop,
      pay_rate: round2(shop ? seededFloat(`${name}|rate`, 26, 44) : seededFloat(`${name}|rate`, 22, 38)),
    });
  }
  // Anyone on the clock who has no profile row still shows up on payroll.
  for (const [name, isShop] of shopHint) {
    if (byName.has(name)) continue;
    byName.set(name, {
      user_id: `qbt-${qbIds.get(name) ?? name}`,
      name,
      qbtime_user_id: qbIds.get(name) ?? SHOP_JOBCODE_ID,
      shop: isShop,
      pay_rate: round2(seededFloat(`${name}|rate`, 22, 42)),
    });
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
});

async function payload(key: PeriodKey, today: Date) {
  const [people, model] = await Promise.all([roster(), revenueModel()]);
  const { start, end } = periodBounds(key, today);
  const days = weekdays(start, end);
  const weekly = key === 'thisWeek' || key === 'lastWeek';

  const employees = people.map((emp) => {
    // Bucket the days by ISO week so overtime is decided per week, not per period.
    const byWeek = new Map<string, number>();
    let pto = 0;
    let workedDays = 0;
    for (const d of days) {
      const date = iso(d);
      const { worked, pto: ptoHours } = dayHours(emp, date);
      pto += ptoHours;
      if (worked <= 0) continue;
      workedDays += 1;
      const wk = iso(startOfWeek(d));
      byWeek.set(wk, (byWeek.get(wk) ?? 0) + worked);
    }
    let regular = 0, ot = 0;
    for (const hours of byWeek.values()) {
      regular += Math.min(OT_THRESHOLD_HOURS, hours);
      ot += Math.max(0, hours - OT_THRESHOLD_HOURS);
    }
    // Unpaid lunch is only netted out on the weekly payroll views.
    if (weekly) regular = Math.max(0, regular - workedDays * UNPAID_LUNCH_HOURS);

    regular = round2(regular);
    ot = round2(ot);
    const total = round2(regular + ot);
    const shopShare = emp.shop ? seededFloat(`${emp.name}|shopshare`, 0.72, 0.96)
      : seededFloat(`${emp.name}|shopshare`, 0.04, 0.18);
    const shopHours = round2(total * shopShare);
    return {
      user_id: emp.user_id,
      name: emp.name,
      regular_hours: regular,
      ot_hours: ot,
      dt_hours: 0,
      pto_hours: round2(pto),
      total_hours: total,
      field_hours: round2(total - shopHours),
      shop_hours: shopHours,
      pay_rate: emp.pay_rate,
      estimated_cost: round2(regular * emp.pay_rate + ot * emp.pay_rate * OT_MULTIPLIER),
    };
  }).filter((e) => e.total_hours > 0 || e.pto_hours > 0);

  const totals = employees.reduce((t, e) => ({
    regular_hours: round2(t.regular_hours + e.regular_hours),
    ot_hours: round2(t.ot_hours + e.ot_hours),
    dt_hours: 0,
    pto_hours: round2(t.pto_hours + e.pto_hours),
    total_hours: round2(t.total_hours + e.total_hours),
    field_hours: round2(t.field_hours + e.field_hours),
    shop_hours: round2(t.shop_hours + e.shop_hours),
    estimated_cost: round2(t.estimated_cost + e.estimated_cost),
    employees: t.employees + 1,
  }), {
    regular_hours: 0, ot_hours: 0, dt_hours: 0, pto_hours: 0, total_hours: 0,
    field_hours: 0, shop_hours: 0, estimated_cost: 0, employees: 0,
  });

  // Divisions: field hours split by each division's share of period revenue.
  const periodMonths = new Set(days.map((d) => iso(d).slice(0, 7)));
  const revenueByDivision = new Map<string, number>();
  for (const m of periodMonths) {
    for (const [div, amt] of model.byMonthDivision.get(m) ?? []) {
      revenueByDivision.set(div, (revenueByDivision.get(div) ?? 0) + amt);
    }
  }
  const revenueTotal = [...revenueByDivision.values()].reduce((s, v) => s + v, 0);
  const blendedRate = totals.total_hours > 0 ? totals.estimated_cost / totals.total_hours : 0;
  const divisions: {
    division: string; hours: number; field: boolean; estimated_cost: number;
    revenue: number | null; direct_labor_pct: number | null;
  }[] = [...revenueByDivision.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([code, revenue]) => {
      const share = revenueTotal > 0 ? revenue / revenueTotal : 0;
      const hours = round2(totals.field_hours * share);
      const cost = round2(hours * blendedRate);
      return {
        division: model.divisionLabels.get(code) ?? code,
        hours,
        field: true,
        estimated_cost: cost,
        revenue: round2(revenue),
        direct_labor_pct: revenue > 0 ? round2((cost / revenue) * 100) : null,
      };
    });
  divisions.push({
    division: 'Shop / Admin',
    hours: totals.shop_hours,
    field: false,
    estimated_cost: round2(totals.shop_hours * blendedRate),
    revenue: null,
    direct_labor_pct: null,
  });

  return {
    asOf: new Date().toISOString(),
    period_key: key,
    period_start: iso(start),
    period_end: iso(end),
    source: 'qbtime' as const,
    totals,
    employees: employees.sort((a, b) => b.total_hours - a.total_hours),
    divisions,
    simulated: true,
  };
}

export const simulatedTime = once(async () => {
  const today = new Date(`${nyDate()}T12:00:00`);
  const snapshots = await Promise.all(PERIODS.map(async (key) => {
    const p = await payload(key, today);
    return {
      period_start: p.period_start,
      period_end: p.period_end,
      period_key: key,
      updated_at: p.asOf,
      payload: p,
    };
  }));

  return {
    ok: true,
    qbtime_configured: true,
    qb_configured: true,
    simulated: true,
    demo_note:
      'Roster and shop/field split come from time_clock_snapshots (shop jobcode 3668547) and profiles; hours are '
      + 'generated per employee per day from a fixed seed, >40h/week becomes OT, an unpaid 30-min lunch is netted '
      + 'out on the weekly views only, and division revenue is real job_invoices revenue.',
    snapshots,
  };
});

export async function simulatedTimeSync() {
  const status = await simulatedTime();
  return {
    ok: true,
    simulated: true,
    asOf: status.snapshots[0]?.updated_at ?? new Date().toISOString(),
    demo_note: 'Sync is a no-op in the demo; QuickBooks Time hours are regenerated deterministically.',
  };
}
